import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useI18n } from '../context/I18nContext';
import { API_BASE } from '../config';
import SpinningLogo from './SpinningLogo';

export default function Sidebar() {
  const {
    agents, personas, rounds, setRounds, setScreen,
    sessionId, participants, setShowHelp, setShowPricing, setShowCharter, logout, isAdmin, canConfigure, pendingCount, inviteUserToSession, setShowContactManager,
    customLogoImage, setShowLogoUploader,
    user, tokenUsage, sessionStart, trialStatus, closeSession, cancelDebate
  } = useApp();
  const { t } = useI18n();
  const [subscribers, setSubscribers] = useState([]);
  const [invitedIds, setInvitedIds] = useState(new Set());
  const [inviteList, setInviteList] = useState([]);
  const [selectedInvitee, setSelectedInvitee] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    const token = localStorage.getItem('cogency_token');
    fetch(`${API_BASE}/api/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const list = data?.users || data;
        if (Array.isArray(list)) setSubscribers(list.filter(u => u.role !== 'pending' && u.role !== 'admin'));
      })
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    const token = localStorage.getItem('cogency_token');
    if (isAdmin) {
      // Super-admins see all verified non-admin users
      fetch(`${API_BASE}/api/users`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(data => {
          if (Array.isArray(data)) {
            setInviteList(data.filter(u => u.role !== 'pending' && u.role !== 'admin'));
          }
        })
        .catch(() => {});
    } else {
      // Regular users see only their collaborators/contacts
      fetch(`${API_BASE}/api/collaborators`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(data => {
          if (Array.isArray(data)) setInviteList(data);
        })
        .catch(() => {});
    }
  }, [isAdmin]);

  const handleInvite = (userId) => {
    if (!userId) return;
    inviteUserToSession(userId, (res) => {
      if (res?.success) {
        setInvitedIds(prev => new Set(prev).add(userId));
      }
    });
  };

  const handleDropdownInvite = () => {
    if (!selectedInvitee) return;
    handleInvite(selectedInvitee);
    setSelectedInvitee('');
  };

  const roleLabel = user?.role
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
    : '';
  const fmtTokens = (n) => (n === null || n === undefined ? '∞' : n.toLocaleString());

  const formatDuration = (totalSeconds) => {
    const s = Math.max(0, Math.floor(totalSeconds));
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;
    const pad = (n) => n.toString().padStart(2, '0');
    if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}`;
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const remainingSeconds = trialStatus?.remainingSeconds;
  let timeLabel = '';
  let timeValue = '';
  if (typeof remainingSeconds === 'number' && remainingSeconds > 0) {
    timeLabel = t('remainingConnexionTime') || 'Remaining time';
    timeValue = formatDuration(remainingSeconds);
  } else if (sessionStart) {
    timeLabel = t('connectedTime') || 'Connection time';
    timeValue = formatDuration((now - sessionStart) / 1000);
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <button
          type="button"
          className="sidebar-logo-btn"
          onClick={() => setShowLogoUploader(true)}
          title={t('changeLogo') || 'Changer le logo'}
          aria-label={t('changeLogo') || 'Changer le logo'}
        >
          <SpinningLogo size={110} imageUrl={customLogoImage || undefined} />
        </button>
        <div className="sidebar-user-info">
          <div className="sidebar-user-name">{user?.name || t('appName')}</div>
          {roleLabel && <div className="sidebar-user-role">{roleLabel}</div>}
        </div>
      </div>

      <div className="sidebar-section">
        <h4>{t('yourAgents')}</h4>
        <div id="sidebarAgents">
          {agents.filter(Boolean).map((agent, i) => {
            const persona = personas.find(p => p.id === agent.persona);
            return (
              <div key={i} className="sidebar-agent">
                <div className="sidebar-agent-dot" style={{ background: persona?.color || '#666' }}></div>
                <div>
                  <div className="sidebar-agent-name">{persona?.name || 'Agent'}</div>
                  <div className="sidebar-agent-model">{(agent.model || '').split(':').pop()}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="setting-row" style={{ marginTop: '12px' }}>
          <label>{t('rounds')}</label>
          <select value={rounds} onChange={e => setRounds(parseInt(e.target.value))} disabled={!canConfigure}>
            {[2, 5, 10, 15, 30, 35, 50, 100, -1].map(n => (
              <option key={n} value={n}>{n === -1 ? '∞' : n}</option>
            ))}
          </select>
        </div>
        <button
          className="btn-primary btn-small"
          style={{ width: '100%', marginTop: '10px' }}
          onClick={() => { cancelDebate(); closeSession(); }}
        >
          ⚙️ {t('reconfigure')}
        </button>

        <h4 style={{ marginTop: '18px', marginBottom: '8px' }}>{t('session')}</h4>
        <div className="session-info">
          <div className="session-id" title={t('copy')}>
            {sessionId ? sessionId.slice(0, 16) + '…' : t('noActiveSession')}
          </div>
        </div>
        <div className="participants-list">
          {participants.length === 0 && <p className="empty-participants">{t('noParticipantsYet')}</p>}
          {participants.map((p, i) => (
            <div key={i} className="participant-item">
              <span className={`participant-dot ${p.type}`}></span>
              <span className="participant-name">{p.name}</span>
              <span className="participant-type">{p.type}</span>
            </div>
          ))}
        </div>
      </div>

      {isAdmin && (
        <div className="sidebar-section">
          <h4>{t('adminPanel')}</h4>
          <div className="setting-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{t('verifyUsers')}</span>
            <span style={{
              background: pendingCount > 0 ? '#ef4444' : '#10b981',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: '999px',
              minWidth: '20px',
              textAlign: 'center'
            }}>{pendingCount}</span>
          </div>
        </div>
      )}

      <div className="sidebar-section">
        <h4>{t('inviteToSession')}</h4>
        {inviteList.length === 0 ? (
          <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{t('noContactsToInvite')}</p>
        ) : (
          <>
            <div className="invite-dropdown-row">
              <select
                className="invite-dropdown"
                value={selectedInvitee}
                onChange={e => setSelectedInvitee(e.target.value)}
              >
                <option value="">{t('selectUser')}</option>
                {inviteList.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name} {u.category ? `(${t(u.category)})` : ''}
                  </option>
                ))}
              </select>
              <button
                className="btn-primary btn-small"
                onClick={handleDropdownInvite}
                disabled={!selectedInvitee}
              >
                {t('invite')}
              </button>
            </div>
            {invitedIds.size > 0 && (
              <p style={{ fontSize: '12px', color: '#10b981', marginTop: '6px' }}>
                ✅ {invitedIds.size} {t('invited')}
              </p>
            )}
          </>
        )}
        <button
          className="btn-ghost btn-small"
          style={{ width: '100%', marginTop: '10px' }}
          onClick={() => setShowContactManager(true)}
        >
          👥 {t('manageContacts')}
        </button>
      </div>

      <div className="sidebar-stats sidebar-stats-bottom">
        <div className="sidebar-stats-label">📊 {t('sessionStats') || 'Session'}</div>
        <div className="sidebar-token-badge" title={t('tokens') || 'Tokens'}>
          {tokenUsage
            ? `${fmtTokens(tokenUsage.remainingTokens)} / ${fmtTokens(tokenUsage.totalTokens)}`
            : '— / —'}
        </div>
        {timeValue && (
          <div className="sidebar-time-badge" title={timeLabel}>
            {timeLabel}: {timeValue}
          </div>
        )}
      </div>

    </aside>
  );
}
